import React, { useMemo } from 'react';
import { usePostHogQuery } from '../../hooks/usePostHogQuery';
import { buildTrendsQuery, buildFunnelsQuery } from '../../utils/posthogApi';
import KpiCard from './components/KpiCard';
import TrendChart from './components/TrendChart';
import FunnelChart from './components/FunnelChart';
import { formatDate, parseFunnelSteps } from './utils';

const EngagementView = ({ dateRange }) => {
  const dateFrom = dateRange || '-30d';

  // ── Content Creation metrics ──
  const { data: creationData, loading: creationLoading } = usePostHogQuery(
    () => buildTrendsQuery(
      [
        { event: 'artwork_uploaded', math: 'total' },
        { event: 'artwork_uploaded', math: 'dau' },
        { event: 'channel_post_created', math: 'total' },
      ],
      dateFrom
    ),
    { deps: [dateFrom] }
  );

  // ── Content Consumption metrics ──
  const { data: consumptionData, loading: consumptionLoading } = usePostHogQuery(
    () => buildTrendsQuery(
      [
        { event: 'artwork_viewed', math: 'total' },
        { event: 'artwork_viewed', math: 'dau' },
        { event: 'event_viewed', math: 'total' },
        { event: 'channel_detail_viewed', math: 'total' },
      ],
      dateFrom
    ),
    { deps: [dateFrom] }
  );

  // ── Channel engagement ──
  const { data: channelData, loading: channelLoading } = usePostHogQuery(
    () => buildTrendsQuery(
      [
        { event: 'channel_joined', math: 'total' },
        { event: 'channel_chat_message_sent', math: 'total' },
        { event: 'channel_comment_added', math: 'total' },
        { event: 'channel_post_upvoted', math: 'total' },
      ],
      dateFrom
    ),
    { deps: [dateFrom] }
  );

  // ── Submission funnel ──
  const { data: submissionFunnel, loading: submissionFunnelLoading } = usePostHogQuery(
    () => buildFunnelsQuery(
      ['submission_started', 'submission_completed'],
      dateFrom,
      { windowDays: 7 }
    ),
    { deps: [dateFrom] }
  );

  // ── Commerce funnel ──
  const { data: commerceFunnel, loading: commerceFunnelLoading } = usePostHogQuery(
    () => buildFunnelsQuery(
      ['merch_card_tapped', 'merch_checkout_started', 'ticket_purchased'],
      dateFrom,
      { windowDays: 7 }
    ),
    { deps: [dateFrom] }
  );

  // ── Public feed funnel ──
  const { data: feedFunnel, loading: feedFunnelLoading } = usePostHogQuery(
    () => buildFunnelsQuery(
      ['public_feed_viewed', 'public_feed_post_tapped', 'public_feed_login_prompted', 'signup_started'],
      dateFrom,
      { windowDays: 14 }
    ),
    { deps: [dateFrom] }
  );

  // ── Derived values ──

  const uploadsTotal = creationData?.results?.[0]?.count ?? 0;
  const uniqueUploaders = creationData?.results?.[1]?.count ?? 0;
  const channelPosts = creationData?.results?.[2]?.count ?? 0;

  const artViewsTotal = consumptionData?.results?.[0]?.count ?? 0;
  const uniqueViewers = consumptionData?.results?.[1]?.count ?? 0;
  const eventViews = consumptionData?.results?.[2]?.count ?? 0;
  const channelViews = consumptionData?.results?.[3]?.count ?? 0;

  const channelJoins = channelData?.results?.[0]?.count ?? 0;
  const channelMessages = channelData?.results?.[1]?.count ?? 0;
  const channelComments = channelData?.results?.[2]?.count ?? 0;
  const channelUpvotes = channelData?.results?.[3]?.count ?? 0;

  const creatorConsumerRatio = uniqueViewers > 0
    ? (uniqueUploaders / uniqueViewers).toFixed(3)
    : '—';

  const channelChartData = useMemo(() => {
    if (!channelData?.results?.[0]) return [];
    const labels = channelData.results[0]?.labels || [];
    return labels.map((label, i) => ({
      date: formatDate(label),
      joins: channelData.results[0]?.data?.[i] ?? 0,
      messages: channelData.results[1]?.data?.[i] ?? 0,
      comments: channelData.results[2]?.data?.[i] ?? 0,
    }));
  }, [channelData]);

  return (
    <div className="bi-engagement">
      {/* Content Creation KPIs */}
      <div className="bi-section">
        <h2 className="bi-section-title">Content Creation</h2>
        <div className="bi-kpi-grid">
          <KpiCard title="Art Uploads" value={uploadsTotal} loading={creationLoading} />
          <KpiCard title="Unique Uploaders" value={uniqueUploaders} loading={creationLoading} />
          <KpiCard title="Channel Posts" value={channelPosts} loading={creationLoading} />
          <KpiCard title="Creator/Consumer Ratio" value={creatorConsumerRatio} loading={creationLoading || consumptionLoading} />
        </div>
      </div>

      {/* Content Consumption KPIs */}
      <div className="bi-section">
        <h2 className="bi-section-title">Content Consumption</h2>
        <div className="bi-kpi-grid">
          <KpiCard title="Art Views" value={artViewsTotal} loading={consumptionLoading} />
          <KpiCard title="Unique Viewers" value={uniqueViewers} loading={consumptionLoading} />
          <KpiCard title="Event Views" value={eventViews} loading={consumptionLoading} />
          <KpiCard title="Channel Views" value={channelViews} loading={consumptionLoading} />
        </div>
      </div>

      {/* Channel Engagement */}
      <div className="bi-section">
        <h2 className="bi-section-title">Channel Engagement</h2>
        <div className="bi-kpi-grid">
          <KpiCard title="New Members" value={channelJoins} loading={channelLoading} />
          <KpiCard title="Messages Sent" value={channelMessages} loading={channelLoading} />
          <KpiCard title="Comments" value={channelComments} loading={channelLoading} />
          <KpiCard title="Upvotes" value={channelUpvotes} loading={channelLoading} />
        </div>
        <TrendChart
          title="Channel Activity Over Time"
          data={channelChartData}
          lines={[
            { key: 'joins', label: 'Joins', color: '#6366f1' },
            { key: 'messages', label: 'Messages', color: '#10b981' },
            { key: 'comments', label: 'Comments', color: '#f59e0b' },
          ]}
          loading={channelLoading}
        />
      </div>

      {/* Funnels */}
      <div className="bi-section">
        <h2 className="bi-section-title">Conversion Funnels</h2>
        <div className="bi-charts-grid">
          <FunnelChart
            title="Submission Funnel"
            steps={parseFunnelSteps(submissionFunnel)}
            loading={submissionFunnelLoading}
          />
          <FunnelChart
            title="Commerce Funnel"
            steps={parseFunnelSteps(commerceFunnel)}
            loading={commerceFunnelLoading}
          />
          <FunnelChart
            title="Public Feed → Signup"
            steps={parseFunnelSteps(feedFunnel)}
            loading={feedFunnelLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default EngagementView;
